import { useSyncExternalStore, type ReactNode } from 'react';
import {
  customerSorts,
  type CustomerList,
  type CustomerSort,
  type SortDir,
} from './customer-types';
import { showingWord, sortWord } from './customer-words';
import { CustomerCards } from './CustomerCards';
import { CustomerTable, firstDir } from './CustomerTable';
import type { CompanyLink } from './customer-cells';
import './customers.css';

/** How many companies a page of the list holds. */
export const CUSTOMER_PAGE_SIZE = 50;

export interface CustomerRowsProps {
  /** The page on screen; absent while the first one loads. */
  list: CustomerList | undefined;
  /** The rows shown are the last answer while the next loads. */
  stale: boolean;
  /** What failed, in the server's words; null when nothing did. */
  error: string | null;
  onRetry: () => void;
  today: string;
  sort: CustomerSort;
  dir: SortDir;
  onSort: (sort: CustomerSort, dir: SortDir) => void;
  /** 1 for the first page. */
  page: number;
  onPage: (page: number) => void;
  /** Whether anything narrows the list, so "nothing matches" can offer to clear it. */
  filtered: boolean;
  onClear: () => void;
  /** The CSV of every row the filters leave, in this order: a plain link the session cookie goes with. */
  csvHref: string;
  onOpen: (id: string) => void;
  renderLink: CompanyLink;
}

const PHONE = '(max-width: 640px)';

/** True at a phone's width, so the list renders cards or the table, never both. */
function usePhone(): boolean {
  return useSyncExternalStore(
    (changed) => {
      const media = globalThis.matchMedia?.(PHONE);
      media?.addEventListener('change', changed);
      return () => media?.removeEventListener('change', changed);
    },
    () => globalThis.matchMedia?.(PHONE).matches ?? false,
    () => false,
  );
}

/** The next order a press on a heading asks for: the other way when it is already the order. */
export function nextSort(
  current: { sort: CustomerSort; dir: SortDir },
  pressed: CustomerSort,
): { sort: CustomerSort; dir: SortDir } {
  if (current.sort !== pressed) return { sort: pressed, dir: firstDir(pressed) };
  return { sort: pressed, dir: current.dir === 'asc' ? 'desc' : 'asc' };
}

function Skeleton(): ReactNode {
  return (
    <div className="ad-cu-skeleton" role="status" aria-label="Loading the customers">
      {Array.from({ length: 8 }, (_, i) => (
        <span key={i} className="ad-cu-skeleton__row" />
      ))}
    </div>
  );
}

function Failed({ error, onRetry }: Pick<CustomerRowsProps, 'error' | 'onRetry'>): ReactNode {
  return (
    <div className="ad-cu-note" role="alert">
      <strong>Could not load the customers.</strong>
      <span>{error}</span>
      <button type="button" className="ad-cu-link" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

function Nothing({ filtered, onClear }: Pick<CustomerRowsProps, 'filtered' | 'onClear'>) {
  return (
    <div className="ad-cu-note">
      <strong>{filtered ? 'No company matches these filters.' : 'No companies yet.'}</strong>
      {filtered ? (
        <button type="button" className="ad-cu-link" onClick={onClear}>
          Clear filters
        </button>
      ) : null}
    </div>
  );
}

/** First, Previous, Next, Last: every page is one press or two away, whatever the count. */
function Pager({ list, page, onPage }: Pick<CustomerRowsProps, 'list' | 'page' | 'onPage'>) {
  const last = Math.max(1, Math.ceil((list?.total ?? 0) / CUSTOMER_PAGE_SIZE));
  if (last === 1) return null;
  const step = (label: string, to: number, off: boolean): ReactNode => (
    <button type="button" className="ad-cu-link" disabled={off} onClick={() => onPage(to)}>
      {label}
    </button>
  );
  return (
    <nav className="ad-cu-pager" aria-label="Pages of customers">
      {step('First', 1, page <= 1)}
      {step('Previous', page - 1, page <= 1)}
      <span className="ad-cu-pager__where">
        Page {page.toLocaleString('en-US')} of {last.toLocaleString('en-US')}
      </span>
      {step('Next', page + 1, page >= last)}
      {step('Last', last, page >= last)}
    </nav>
  );
}

/** On a phone there are no headings to press, so the order is a small menu. */
function SortMenu({ sort, dir, onSort }: Pick<CustomerRowsProps, 'sort' | 'dir' | 'onSort'>) {
  return (
    <select
      className="ad-cu-select"
      aria-label="Order"
      value={`${sort}.${dir}`}
      onChange={(e) => {
        const [s, d] = e.currentTarget.value.split('.');
        onSort(s as CustomerSort, d as SortDir);
      }}
    >
      {customerSorts.flatMap((s) =>
        (['desc', 'asc'] as const).map((d) => (
          <option key={`${s}.${d}`} value={`${s}.${d}`}>
            {sortWord[s]} {d === 'asc' ? '↑' : '↓'}
          </option>
        )),
      )}
    </select>
  );
}

function Body(props: CustomerRowsProps & { phone: boolean }): ReactNode {
  const { list, today, onOpen, renderLink } = props;
  if (props.error && !list) return <Failed error={props.error} onRetry={props.onRetry} />;
  if (!list) return <Skeleton />;
  if (list.rows.length === 0) return <Nothing filtered={props.filtered} onClear={props.onClear} />;
  const shared = { rows: list.rows, today, onOpen, renderLink };
  return props.phone ? (
    <CustomerCards {...shared} />
  ) : (
    <CustomerTable
      {...shared}
      sort={props.sort}
      dir={props.dir}
      onSort={(pressed) => {
        const next = nextSort(props, pressed);
        props.onSort(next.sort, next.dir);
      }}
    />
  );
}

/**
 * The list: fifty companies a page, searched, filtered and sorted by the ledger. A table, or cards on a
 * phone. The last page stays up, dimmed, while the next loads; nothing found and a failed read say so.
 */
export function CustomerRows(props: CustomerRowsProps): ReactNode {
  const { list } = props;
  const phone = usePhone();
  return (
    <section
      className={`ad-cu-list${props.stale ? ' ad-cu-stale' : ''}`}
      aria-label="Customers"
      aria-busy={props.stale || !list ? true : undefined}
    >
      <div className="ad-cu-list__bar">
        <span className="ad-cu-list__count" aria-live="polite">
          {list ? showingWord(list.offset, list.rows.length, list.total) : 'Loading'}
        </span>
        {phone ? <SortMenu {...props} /> : null}
        <a className="ad-cu-link" href={props.csvHref} download>
          Download CSV
        </a>
      </div>
      {props.error && list ? <Failed error={props.error} onRetry={props.onRetry} /> : null}
      <Body {...props} phone={phone} />
      <Pager list={list} page={props.page} onPage={props.onPage} />
    </section>
  );
}
